import { useEffect, useMemo, useRef, useState } from "react";
import { Monitor, Smartphone } from "lucide-react";
import clsx from "clsx";
import {
  Alert,
  Card,
  InlineLoading,
  SegmentedControl,
  Switch,
  Tabs,
  Text,
  Tooltip,
} from "@marekl/adminer/components";
import { formatBytes } from "@marekl/adminer/format";
import {
  usePreviewTemplateDraft,
  type DraftPreviewRequest,
  type PreviewResponse,
} from "../../lib/queries/templates";
import { ApiError } from "../../lib/api";
import styles from "./TemplateDetailPage.module.css";

const DEBOUNCE_MS = 700;

interface PreviewPaneProps {
  /** Current editor content; `null` while the editor is still loading. */
  request: DraftPreviewRequest | null;
  /** Set when the sample-data JSON is invalid (preview uses last valid data). */
  sampleDataWarning?: string | null;
}

type PreviewTab = "html" | "text";
type PreviewWidth = "desktop" | "mobile";

function previewErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: unknown } | null;
    if (body && typeof body.message === "string" && body.message) {
      return body.message;
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return "The preview failed to render.";
}

/**
 * Live preview: debounced POST /api/templates/preview with the current
 * (unsaved) editor state. Template errors (422) render inline in the
 * pane — the last successful render stays visible underneath.
 */
export function PreviewPane({ request, sampleDataWarning }: PreviewPaneProps) {
  const preview = usePreviewTemplateDraft();
  const [tab, setTab] = useState<PreviewTab>("html");
  const [width, setWidth] = useState<PreviewWidth>("desktop");
  const [marketing, setMarketing] = useState(false);
  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const fullRequest = useMemo<DraftPreviewRequest | null>(
    () => (request ? { ...request, marketing } : null),
    [request, marketing],
  );
  const requestKey = useMemo(
    () => (fullRequest ? JSON.stringify(fullRequest) : ""),
    [fullRequest],
  );
  const mutateAsync = preview.mutateAsync;

  useEffect(() => {
    if (!fullRequest) return;
    const seq = ++seqRef.current;
    const timer = setTimeout(() => {
      mutateAsync(fullRequest)
        .then((res) => {
          if (seq !== seqRef.current) return;
          setResult(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (seq !== seqRef.current) return;
          setError(previewErrorMessage(err));
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on requestKey (the request's JSON) instead of fullRequest, whose identity changes every render
  }, [requestKey, mutateAsync]);

  return (
    <Card>
      <Card.Header
        title="Preview"
        actions={
          preview.isPending ? (
            <InlineLoading>Updating…</InlineLoading>
          ) : undefined
        }
      />
      <Card.Content>
        <div className={styles.previewStack}>
          <div className={styles.previewToolbar}>
            <Tabs
              aria-label="Preview format"
              tabs={[
                { id: "html", label: "HTML" },
                { id: "text", label: "Text" },
              ]}
              value={tab}
              onChange={(v) => setTab(v as PreviewTab)}
            />
            <div className={styles.previewToolbarRight}>
              {tab === "html" && (
                <SegmentedControl<PreviewWidth>
                  aria-label="Preview width"
                  size="sm"
                  value={width}
                  onChange={setWidth}
                  options={[
                    {
                      value: "desktop",
                      label: "Desktop",
                      icon: <Monitor size={14} aria-hidden="true" />,
                    },
                    {
                      value: "mobile",
                      label: "Mobile",
                      icon: <Smartphone size={14} aria-hidden="true" />,
                    },
                  ]}
                />
              )}
              <Tooltip content="Render with a sample unsubscribe link">
                {/* Tooltip needs a ref-forwarding trigger; Switch's ref lands on
                    its hidden input, so the span carries the hover handlers. */}
                <span style={{ display: "inline-flex" }}>
                  <Switch
                    label="Marketing"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                  />
                </span>
              </Tooltip>
            </div>
          </div>

          {sampleDataWarning && (
            <Alert variant="warn">{sampleDataWarning}</Alert>
          )}
          {error && (
            <Alert variant="err" title="Template error">
              {error}
            </Alert>
          )}

          {result ? (
            <>
              <div className={styles.previewMeta}>
                <div>
                  <Text variant="muted">Subject:</Text>{" "}
                  {result.subject || <Text variant="muted">(empty)</Text>}
                </div>
                {result.preheader && (
                  <div>
                    <Text variant="muted">Preheader:</Text> {result.preheader}
                  </div>
                )}
              </div>

              {tab === "html" ? (
                <div
                  className={clsx(
                    styles.previewFrameWrap,
                    width === "mobile" && styles.previewFrameMobile,
                  )}
                >
                  <iframe
                    className={styles.previewFrame}
                    sandbox=""
                    srcDoc={result.html}
                    title="Email preview"
                    style={width === "mobile" ? { width: 375 } : undefined}
                  />
                </div>
              ) : (
                <pre className={styles.previewText}>
                  {result.text || "(empty text part)"}
                </pre>
              )}

              <div className={styles.previewFooter}>
                {typeof result.size_bytes === "number" && (
                  <Text variant={result.size_warning ? undefined : "muted"}>
                    HTML size: {formatBytes(result.size_bytes)}
                  </Text>
                )}
                {result.size_warning && (
                  <Alert variant="warn">{result.size_warning}</Alert>
                )}
              </div>
            </>
          ) : !error ? (
            <Text variant="muted">Loading preview…</Text>
          ) : null}
        </div>
      </Card.Content>
    </Card>
  );
}
